import { FormikErrors } from 'formik'
import { useCallback, useState } from 'react'
import { TripleImagePageElement } from 'types'
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Fade,
  TextField,
  Typography,
} from '@mui/material'
import {
  AvailablePhotos,
  ReadonlySelectedPhotos,
  SelectedPhotos,
  useChooseAvailablePhotos,
} from '../../../../../shared/GalleryComponents'
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels'

export function TripleImagePageElementDetail(props: {
  element?: TripleImagePageElement
  editMode: boolean
  errors?: FormikErrors<TripleImagePageElement>
  onUpdateField: (field: string, value: any) => void
}) {
  const [showMaxImageError, setShowMaxImageError] = useState(false)

  const updateSelectedPhotoIds = useCallback(
    (photoIds: string[]) => {
      if (photoIds.length > 3) {
        setShowMaxImageError(true)
        setTimeout(() => setShowMaxImageError(false), 3000)

        return
      }
      props.onUpdateField('photoIds', photoIds)
    },
    [props]
  )

  const {
    availablePhotosWithSelected,
    loading,
    availablePhotosFetchError,
    onTogglePhoto,
  } = useChooseAvailablePhotos({
    selectedPhotoIds: props.element!.photoIds,
    setSelectedPhotoIds: updateSelectedPhotoIds,
  })

  return (
    <Box>
      <TextField
        required={props.editMode}
        multiline
        fullWidth
        error={!!props.errors?.text}
        value={props.element?.text}
        label="Contents"
        margin="dense"
        onChange={event => props.onUpdateField('text', event.target.value)}
        helperText="Subtitle text displayed under the images."
        inputProps={{ readOnly: !props.editMode }}
      />
      <Typography variant="subtitle1">Photos</Typography>
      {showMaxImageError && (
        <Fade in={showMaxImageError} timeout={300}>
          <Alert severity="error">Maximum of 3 images allowed.</Alert>
        </Fade>
      )}

      {loading && props.editMode ? (
        <CircularProgress />
      ) : availablePhotosFetchError && props.editMode ? (
        <Alert severity="error">
          Failed to load available photos. Please try again later.
        </Alert>
      ) : props.editMode ? (
        <PanelGroup
          direction="horizontal"
          autoSaveId="gallery-page-element-detail"
        >
          <Panel defaultSize={1}>
            <SelectedPhotos
              selectedPhotos={props.element!.photoIds}
              updateSelectedPhotoIds={updateSelectedPhotoIds}
              onTogglePhoto={onTogglePhoto}
            />
          </Panel>
          <PanelResizeHandle>
            <Divider orientation="vertical" sx={{ margin: '0 8px' }} />
          </PanelResizeHandle>

          <Panel defaultSize={7}>
            <AvailablePhotos
              availablePhotos={availablePhotosWithSelected}
              onTogglePhoto={onTogglePhoto}
            />
          </Panel>
        </PanelGroup>
      ) : (
        <ReadonlySelectedPhotos photoIds={props.element!.photoIds} />
      )}
    </Box>
  )
}
